/**
 * Skills Hub: shared DTOs for the tap-to-run Agent Skills (SKILL.md) feature.
 *
 * Mirrors the Prisma models in `packages/db/prisma/schema/skills.prisma`. The
 * SKILL.md manifest is the per-skill agent context + tool allow-list; CV data
 * only ever enters as tool inputs, so none of these DTOs carry raw CV content.
 *
 * Runtime const arrays (SKILL_SHELVES, SKILL_TIERS, …) are re-exported through
 * `skill.runtime.js` so nodenext ESM consumers resolve them: import the values
 * from `@tabbio/types` (which points at the `.runtime.js`) and the types from
 * here.
 */

import type { CVAccentColor, CVLayoutStyle, CvSectionId } from './cv';

// ── Enumerations (const arrays back the unions; see skill.runtime.js) ──────────

export const SKILL_SHELVES = ['default', 'community', 'own'] as const;
export type SkillShelf = (typeof SKILL_SHELVES)[number];

export const SKILL_TIERS = ['free', 'plus', 'pro'] as const;
export type SkillTier = (typeof SKILL_TIERS)[number];

export const SKILL_STATUSES = [
  'built',
  'new',
  'template',
  'private',
  'in_review',
  'published',
  'disabled',
] as const;
export type SkillStatus = (typeof SKILL_STATUSES)[number];

export const SKILL_VISIBILITIES = ['public', 'private', 'unlisted'] as const;
export type SkillVisibility = (typeof SKILL_VISIBILITIES)[number];

export const SKILL_RUN_STATUSES = [
  'pending',
  'running',
  'completed',
  'failed',
  'blocked',
] as const;
export type SkillRunStatus = (typeof SKILL_RUN_STATUSES)[number];

export const SKILL_ACCESS_WHO_CAN_RUN = [
  'any',
  'verified',
  'default_only',
  'nobody',
] as const;
export type SkillAccessWhoCanRun = (typeof SKILL_ACCESS_WHO_CAN_RUN)[number];

// ── tabbio/v2 catalog enums (docs/TABBIO-SKILLS-DOCUMENTATION.md) ──────────────

export const SKILL_TYPES = ['cv', 'other'] as const;
export type SkillType = (typeof SKILL_TYPES)[number];

export const SKILL_CATEGORIES = [
  'apply',
  'interview',
  'message',
  'general',
] as const;
export type SkillCategory = (typeof SKILL_CATEGORIES)[number];

export const SKILL_COUNTRIES = [
  'uae',
  'ksa',
  'kwt',
  'qat',
  'omn',
  'bhr',
  'gulf',
] as const;
export type SkillCountry = (typeof SKILL_COUNTRIES)[number];

export const SKILL_LEVELS = ['intern', 'fresh', 'mid', 'exec', 'all'] as const;
export type SkillLevel = (typeof SKILL_LEVELS)[number];

export const SKILL_LANGUAGES = ['en', 'ar', 'bi'] as const;
export type SkillLanguage = (typeof SKILL_LANGUAGES)[number];

export const SKILL_RECOMMENDED_MODELS = [
  'lite',
  'flash',
  'gpt',
  'sonnet',
  'gpro',
  'opus',
] as const;
export type SkillRecommendedModel = (typeof SKILL_RECOMMENDED_MODELS)[number];

export const SKILL_CONTEXT_SOURCES = [
  'primary_cv',
  'profile',
  'memory',
] as const;
export type SkillContextSource = (typeof SKILL_CONTEXT_SOURCES)[number];

export const SKILL_LAYOUT_TEMPLATES = [
  'modern',
  'classic',
  'executive',
  'creative',
] as const;
export type SkillLayoutTemplate = (typeof SKILL_LAYOUT_TEMPLATES)[number];

/** CV-skill export layout: template + accent hex + market photo default. */
export interface SkillLayout {
  template: SkillLayoutTemplate;
  accent: string;
  photo: boolean;
}

/** One declarative run-screen input (manifest `inputs[]`, tabbio/v2). */
export interface SkillInputField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  required: boolean;
  placeholder?: string;
  options?: string[];
}

// ── Two-way skills: kind / run / cv_config ────────────────────────────────────
// Mirrors the Zod schemas in @tabbio/utils (skill-manifest). Design:
// docs/skills-two-way-design.md.

/** Skill discriminator: absent/'default' = portable instruction skill. */
export type SkillKind = 'default' | 'cv_custom';

/** Manifest `run` block: drives the run panel instead of the hardcoded map. */
export interface SkillRunConfig {
  input: 'cv' | 'text' | 'none';
  output: 'cv_edit' | 'markdown';
}

/** One entry in a custom CV skill's ordered section include-list. */
export interface SkillCvSectionConfig {
  id: CvSectionId;
  /** Per-section authoring prompt (max 2000 chars, validated in @tabbio/utils). */
  prompt?: string;
}

/**
 * Manifest `cv_config` block (`kind: cv_custom` only). Style/color are the
 * curated `CVConfigData` presets: 6 accent ids, no free hex. `sections` is
 * the ordered include-list; an omitted section is hidden.
 */
export interface SkillCvConfig {
  style: CVLayoutStyle;
  color: CVAccentColor;
  showPhoto: boolean;
  sections: SkillCvSectionConfig[];
}

// ── Manifest (SKILL.md frontmatter) ───────────────────────────────────────────

/**
 * The locked SKILL.md frontmatter shape (schema_version 1). Required keys match
 * what every real built manifest carries; everything else is optional so the
 * Default shelf validates today and authored skills can grow into the richer
 * fields. The runtime Zod validator lives in `@tabbio/utils` (skill-manifest).
 *
 * Note `tier` is free-form text here (real manifests use values like
 * "Plus", "free_basic_or_plus_full", "Free 1/day, Plus unlimited"); the
 * canonical entitlement tier is the normalized `SkillTier` on the Skill row.
 */
export interface SkillManifest {
  /**
   * Optional since the two-way design: a foreign/portable SKILL.md carries
   * only `name` + `description`; importers fill skill_id/tier/locales
   * defaults. Tabbio-built manifests always include all three.
   */
  skill_id?: string;
  name: string;
  description: string;
  tier?: string;
  locales?: string[];
  /** Two-way skills discriminator (docs/skills-two-way-design.md). */
  kind?: SkillKind;
  /** Run-panel spec; resolution order: manifest → seed map → category fallback. */
  run?: SkillRunConfig;
  /** Only for `kind: cv_custom`. */
  cv_config?: SkillCvConfig;
  schema_version?: number;
  skill_version?: string;
  name_ar?: string;
  description_ar?: string;
  status?: string;
  visibility?: string;
  author?: string;
  author_url?: string;
  license?: string;
  region?: string;
  /** Preset icon id (flag | doc | sparkle | briefcase | grad | star). */
  icon_key?: string;
  /** Public CDN URL for a user-uploaded custom icon image. */
  icon_url?: string;
  categories?: string[];
  tags?: string[];
  action_codes?: string[];
  /** Singular alias used by the `_TEMPLATE` manifest; folded into action_codes. */
  action_code?: string;
  /** Per-skill tool allow-list. */
  tools?: string[];
  scopes?: string[];
  md_alignment?: string[];
  subject_support?: string[];
  hand_off_from?: string[];
  hand_off_to?: string[];
  emits_events?: string[];
  resources?: string[];
  /**
   * Legacy manifests carry an object schema; tabbio/v2 carries a declarative
   * list of run-screen fields (SkillInputField[]).
   */
  inputs?: Record<string, unknown> | SkillInputField[];
  outputs?: Record<string, unknown>;
  privacy?: Record<string, unknown>;
  evals?: unknown;
  eval_key?: string;
  // ── tabbio/v2 frontmatter (skill_format: tabbio/v2) ──
  skill_format?: string;
  /** v2 alias of skill_id (folded at parse time). */
  id?: string;
  type?: SkillType;
  /** `flag:<country>` | `cat:<category>` | `image:<asset>` (folded to icon_key/region). */
  icon?: string;
  country?: SkillCountry;
  level?: SkillLevel;
  /** v2 single language enum; `locales` keeps the array form. */
  language?: SkillLanguage;
  category?: SkillCategory;
  recommended_model?: SkillRecommendedModel;
  context?: SkillContextSource[];
  /** v2 alias of author (folded at parse time). */
  maker?: string;
  /** Platform-set; authoritative flag is shelf === 'default'. */
  verified?: boolean;
  /** v2 alias of skill_version (folded at parse time). */
  version?: string;
  layout?: SkillLayout;
  [key: string]: unknown;
}

// ── Skill DTOs ────────────────────────────────────────────────────────────────

/** Card-level shape returned by the Hub list endpoint. */
export interface SkillSummary {
  id: string;
  skillId: string;
  name: string;
  nameAr?: string | null;
  description: string;
  descriptionAr?: string | null;
  version: string;
  shelf: SkillShelf | string;
  categories: string[];
  tags: string[];
  languages: string[];
  region?: string | null;
  /** Preset icon id (flag | doc | sparkle | briefcase | grad | star). */
  iconKey?: string | null;
  /** Public CDN URL for a user-uploaded custom icon image (takes precedence). */
  iconUrl?: string | null;
  tier: SkillTier | string;
  status: SkillStatus | string;
  visibility: SkillVisibility | string;
  authorUserId?: string | null;
  authorName?: string | null;
  /** Platform-managed trust mark; true for reviewed first-party skills. */
  verified: boolean;
  ownerUserId?: string | null;
  ratingAvg?: number | null;
  /** Number of users who installed this skill into My skills. */
  installCount: number;
  /** Whether the authenticated caller installed this skill. Own skills are true. */
  installed: boolean;
  usageCount: number;
  /** Semantic-search relevance score when the list was issued with `q`. */
  relevance?: number;
  // ── tabbio/v2 catalog fields ──
  type: SkillType | string;
  category?: SkillCategory | string | null;
  country?: SkillCountry | string | null;
  level?: SkillLevel | string | null;
  language?: SkillLanguage | string | null;
  recommendedModel?: SkillRecommendedModel | string | null;
  contextSources: string[];
  layout?: SkillLayout | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

/** Full detail shape returned by GET /api/skills/:skillId. */
export interface SkillDetail extends SkillSummary {
  schemaVersion: number;
  actionCodes: string[];
  /** Per-skill tool allow-list. */
  scopes: string[];
  inputsSchema: Record<string, unknown>;
  outputsSchema: Record<string, unknown>;
  privacy: Record<string, unknown>;
  mdAlignment: string[];
  subjectSupport: string[];
  handOffFrom: string[];
  handOffTo: string[];
  emitsEvents: string[];
  authorUrl?: string | null;
  license?: string | null;
  contentStorageKey?: string | null;
  resourceKeys: string[];
  metadata?: Record<string, unknown>;
}

export interface SkillRunToolLogDTO {
  id: string;
  toolName: string;
  /** High-level only: never raw CV content. */
  actionDescription?: string | null;
  status: string;
  createdAt: string | Date;
}

/** A run record. NEVER carries raw CV content: only ids, a diff, and reasons. */
export interface SkillRunDTO {
  id: string;
  skillId: string;
  skillVersionId?: string | null;
  userId: string;
  cvId?: string | null;
  /** Sibling CV produced by the write (parentCvId) = undo target. */
  resultCvId?: string | null;
  inputs: Record<string, unknown>;
  dryRun: boolean;
  status: SkillRunStatus | string;
  diff: Record<string, unknown>;
  reasonCodes: string[];
  toolsCalled: string[];
  confirmedAt?: string | Date | null;
  errorCode?: string | null;
  toolLogs?: SkillRunToolLogDTO[];
  createdAt: string | Date;
  completedAt?: string | Date | null;
}

export interface SkillAccessPreference {
  userId: string;
  whoCanRunSkills: SkillAccessWhoCanRun;
  communitySkillsEnabled: boolean;
  confirmFullCvWrite: boolean;
  blockedSkillIds: string[];
  blockedCreatorIds: string[];
  updatedAt?: string | Date;
}

// ── Request / response envelopes (the `data` payloads under {data,error,meta}) ─

export interface SkillListQuery {
  shelf?: SkillShelf | string;
  category?: string;
  locale?: string;
  tier?: SkillTier | string;
  /** cv | other. Server-side twin of the client `skillType()` discriminator. */
  type?: SkillType | string;
  region?: string;
  /** Free-text query: drives pgvector semantic ranking when present. */
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface SkillListResult {
  skills: SkillSummary[];
  nextCursor?: string | null;
}

export interface SkillInstallationResult {
  skillId: string;
  installed: boolean;
  installCount: number;
}

/** How a CV skill writes: over the CV in place, or as a new named copy. */
export type SkillRunWriteMode = 'update' | 'new_version';

/**
 * What a prepared full-CV run will do, for a client that renders its own
 * confirmation (the English `summary` says the same). Additive: old servers
 * omit it.
 */
export interface SkillPrepareRunReview {
  cvId: string;
  cvTitle: string;
  /** Who the CV is about (the partner's client on a client CV). */
  subjectName: string | null;
  /** The CV's language code (`en`, `ar`). */
  language: string;
  /** Null when the caller did not say (old clients): either mode may run. */
  mode: SkillRunWriteMode | null;
  /** The name a new copy gets, when the caller chose one. */
  copyTitle: string | null;
}

export interface SkillPrepareRunResult {
  summary: string;
  confirmationToken: string;
  expiresAt: string | Date;
  /**
   * The write this confirmation covers. A run with another mode, or after
   * the CV changed, is refused (SKILL_CONFIRMATION_MISMATCH /
   * SKILL_CONFIRMATION_STALE) and needs a new prepare.
   */
  mode?: SkillRunWriteMode | null;
  review?: SkillPrepareRunReview;
}

export interface SkillRunRequest {
  cvId?: string;
  inputs?: Record<string, unknown>;
  idempotencyKey?: string;
  dryRun?: boolean;
  confirmationToken?: string;
}

export interface SkillRunResult {
  runId: string;
  status: SkillRunStatus | string;
  run?: SkillRunDTO;
  diff?: Record<string, unknown>;
  /** Long-running (mock / full-CV) runs return a poll handle instead. */
  taskId?: string;
}

export interface SkillGenerateDraftResult {
  name: string;
  description: string;
  nameAr?: string | null;
  descriptionAr?: string | null;
  kind: SkillKind;
  categories: string[];
  tags: string[];
  recommendedModel: string;
  instructions: string;
  markdown: string;
  cvConfig?: SkillCvConfig | null;
}

